import { useEffect, useState } from "react";
import { getConsent, onConsentChange, type ConsentState } from "@/lib/consent";

/**
 * Conditionally loads Google Analytics 4 only after explicit consent.
 *
 * Wiring: set VITE_GA_MEASUREMENT_ID (e.g. "G-XXXXXXXXXX") in your Netlify
 * environment. If unset, this component is a no-op — safe to mount always.
 *
 * On revocation (user toggles off via Privacy Choices), the GA script remains
 * in the document but we set `ga-disable-<ID> = true` per Google's documented
 * opt-out mechanism, and stop further pageview pushes.
 */
export function AnalyticsLoader() {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as
    | string
    | undefined;
  const [consent, setConsent] = useState<ConsentState | null>(null);

  useEffect(() => {
    setConsent(getConsent());
    return onConsentChange(setConsent);
  }, []);

  useEffect(() => {
    if (!measurementId) return;
    if (!consent) return;

    const disableFlag = `ga-disable-${measurementId}` as keyof Window;

    if (consent.analytics === "granted" && !consent.gpc) {
      (window as unknown as Record<string, unknown>)[disableFlag as string] = false;
      injectGaOnce(measurementId);
    } else {
      (window as unknown as Record<string, unknown>)[disableFlag as string] = true;
    }
  }, [consent, measurementId]);

  return null;
}

function injectGaOnce(measurementId: string): void {
  if (typeof document === "undefined") return;
  const existing = document.querySelector(
    `script[data-dashalign-ga="${measurementId}"]`,
  );
  if (existing) return;

  const loader = document.createElement("script");
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  loader.setAttribute("data-dashalign-ga", measurementId);
  document.head.appendChild(loader);

  const config = document.createElement("script");
  config.setAttribute("data-dashalign-ga-config", measurementId);
  config.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', ${JSON.stringify(measurementId)}, { anonymize_ip: true });
  `;
  document.head.appendChild(config);
}
